import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, {
  forwardRef,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import Button from "../../Button/Button";
import colours from "../../constants/colours";
import { Icon } from "../../Icon";
import {
  mergeProps,
  stripInvalidPropsForElement,
} from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import { FileCard } from "./FileCard";
import { useDragAndDrop } from "./FileInput.helpers";
import styles from "./FileInput.module.scss";
import {
  FileCardProps,
  FileInputProps,
  type ControlledFileInputProps,
} from "./types";
import { nanoid } from "nanoid";

const cx = classNames.bind(styles);

const MAX_FILE_LIMIT = 10;

const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      id,
      name,
      placeholder = "Select or drag files here",
      value: files,
      previewSlot,
      acceptedFileTypes,
      allowMultiple = false,
      hasTrigger = false,
      isAdditive = false,
      leftSlot,
      flairSlot,
      isDisabled = false,
      error,
      invalidMessage,
      "aria-invalid": ariaInvalid,
      isLoading = false,
      isBusy = false,
      onChange,
      onCancel,
      onBlur,
      className,
      style,
      buttonProps,
      ...restProps
    },
    ref
  ) => {
    const actionDetailsId = useRef(nanoid(6));
    const isActuallyDisabled =
      isDisabled ||
      (!allowMultiple && !!files?.length) ||
      (files && files.length >= MAX_FILE_LIMIT);

    const {
      ref: dragAndDropRef,
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      isDraggingOver,
    } = useDragAndDrop({
      disabled: isActuallyDisabled || isLoading,
      multiple: allowMultiple,
      value: files,
      onChange,
    });
    const [shouldShowTrigger, setShouldShowTrigger] = useState(hasTrigger);

    const handleFileSelection = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (isAdditive) {
        onChange?.(
          event.target.files?.length
            ? Array.from(event.target.files)
            : undefined
        );
        return;
      }

      onChange?.(
        event.target.files?.length
          ? allowMultiple && files?.length
            ? [...files, ...event.target.files]
            : event.target.files instanceof FileList
              ? allowMultiple
                ? [...(files ?? []), ...Array.from(event.target.files)]
                : Array.from(event.target.files)
              : undefined
          : undefined
      );
    };

    const handleFileSelectionByDrop = (
      event: React.DragEvent<HTMLDivElement>
    ) => {
      onBlur?.();
      onDrop?.(event);
    };

    const handleFileCardOnChange: FileCardProps["onChange"] = (
      newFile,
      previousFile
    ) => {
      if (!newFile) {
        // handle delete
        onChange?.(
          files?.length
            ? files instanceof FileList
              ? Array.from(files).filter(
                  (oldFile) => oldFile.name !== previousFile?.name
                )
              : files.filter((oldFile) => oldFile.name !== previousFile?.name)
            : undefined
        );
        return;
      }

      // handle swap
      onChange?.(
        files?.length
          ? files instanceof FileList
            ? Array.from(files).map((oldFile) =>
                oldFile.name === previousFile?.name ? newFile : oldFile
              )
            : files.filter((oldFile) =>
                oldFile.name !== previousFile?.name ? newFile : oldFile
              )
          : undefined
      );
    };

    const hasError = !!ariaInvalid || !!error || !!invalidMessage;

    return (
      <div className={cx("file-input-container")}>
        {previewSlot
          ? previewSlot({
              value: files,
              handleOnChange: handleFileCardOnChange,
              isLoading,
              isBusy,
              isDisabled: isActuallyDisabled,
              isValid: !hasError,
              error,
            })
          : files
            ? Array.from(files)?.map((file) => (
                <FileCard
                  key={file.name}
                  file={file}
                  isLoading={isLoading}
                  isBusy={isBusy}
                  isValid={!hasError}
                  onCancel={onCancel}
                  onChange={handleFileCardOnChange}
                  percentageComplete={100}
                  percentageLabel="Uploaded."
                />
              ))
            : null}

        {shouldShowTrigger ? (
          <div className={cx("trigger")}>
            <Icon
              variant="paperclip"
              size={16}
              colour={colours.colours.navy[300]}
            />
            <p
              className={cx("trigger-text")}
              onClick={() => setShouldShowTrigger(false)}
            >
              Add Attachment
            </p>
          </div>
        ) : (
          <div
            className={cx(
              "container",
              {
                "container--error": hasError,
                "container--disabled": isActuallyDisabled,
                "is-dragging-over": isDraggingOver,
              },
              hasTrigger && "container--has-trigger-variant",
              className
            )}
            style={style}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={handleFileSelectionByDrop}
            ref={dragAndDropRef as RefObject<HTMLDivElement>}
          >
            {leftSlot
              ? leftSlot({
                  className: cx(
                    "left-slot",
                    hasTrigger && "left-slot--has-trigger-variant"
                  ),
                })
              : null}
            {isDraggingOver ? (
              <div className={cx("drag-overlay")}>
                <Icon variant="upload-simple" />
                <p className={cx("drag-overlay--text")}>Drop files here</p>
              </div>
            ) : null}
            {leftSlot ? null : (
              <label
                className={cx(
                  "label",
                  hasTrigger && "label--has-trigger-variant"
                )}
                id={actionDetailsId.current}
              >
                <Icon
                  variant="paperclip"
                  size={18}
                  colour={colours.colours.navy[300]}
                  className={cx("label-icon")}
                />
                <p
                  className={cx("label-text")}
                  onClick={() => setShouldShowTrigger(false)}
                >
                  {placeholder}
                </p>
              </label>
            )}

            <Button
              // essentially a hack to make it act like a label which will open
              // the native file picker
              // @ts-expect-error breaking restriction on purpose
              as="label"
              htmlFor={id}
              tabIndex={0}
              size="xx-small"
              className={cx(
                "button",
                hasTrigger && "button--has-trigger-variant"
              )}
              variant="outline"
              disabled={isActuallyDisabled}
              isLoading={isLoading}
              {...(leftSlot
                ? {
                    title: placeholder,
                  }
                : {
                    "aria-details": actionDetailsId.current,
                  })}
              slotRight={
                flairSlot ? flairSlot({ className: cx("flair-slot") }) : null
              }
              {...buttonProps}
            >
              Choose File
            </Button>

            <input
              {...stripInvalidPropsForElement(restProps, "input")}
              tabIndex={-1}
              id={id}
              name={name}
              className={cx("input")}
              placeholder={placeholder}
              onBlur={onBlur}
              ref={ref}
              disabled={isActuallyDisabled}
              onChange={handleFileSelection}
              type="file"
              multiple={allowMultiple}
              accept={acceptedFileTypes}
            />
          </div>
        )}
      </div>
    );
  }
);

FileInput.displayName = "FileInput";

export const ControlledFileInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledFileInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value) ||
                      (Array.isArray(value) && !value.length)
                        ? `${typeof label === "string" ? label : "File input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot, leftSlot }) => (
        <FileInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          flairSlot={flairSlot}
          leftSlot={leftSlot}
          isInline={isInline}
          isFluid={isFluid}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

ControlledFileInput.displayName = "ControlledFileInput";

export default FileInput;
